import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * Whether an element's content currently overflows its own box
 * (`scrollHeight > clientHeight`).
 *
 * Genuine layout measurement — cannot be exercised in jsdom, which computes
 * neither `scrollHeight` nor `clientHeight` (both always 0). Callers' RTL tests
 * should mock this module; the real measurement is covered by a Playwright
 * browser test instead (see `web/CLAUDE.md`, "Browser tests vs co-located RTL").
 *
 * Re-measures via ResizeObserver rather than only at mount, so a change in the
 * element's own rendered size — a column that has no enforced min-width and
 * narrows with the viewport, or the element's own content/class changing —
 * is detected as it happens, not just once.
 *
 * Guards ResizeObserver's existence: jsdom (any RTL test that renders this
 * component tree for real, rather than mocking this module — e.g. CatalogPage's
 * own tests, which switch to list view without mocking it) has no global
 * ResizeObserver, and an unguarded `new ResizeObserver(...)` throws there. The
 * one-time measure() still runs either way — harmless in jsdom, where
 * scrollHeight/clientHeight are both always 0 — it just won't re-measure on
 * resize without a real ResizeObserver.
 */
export function useIsOverflowing(ref: RefObject<HTMLElement | null>): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => setIsOverflowing(el.scrollHeight > el.clientHeight);
    measure();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return isOverflowing;
}
